import { Card } from "@shared/ui/card";
import { Progress } from "@shared/ui/progress";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@shared/ui/popover";
import { Button } from "@shared/ui/button";
import { Calendar } from "@shared/ui/calendar";
import { useState, useEffect } from "react";
import {
  useMeterRunList,
  useRunWiseReadingValidationDashboard,
} from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const ValidationProgress = () => {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [selectedRun, setSelectedRun] = useState<string>("");

  // Add the hooks
  const remoteUtilityId = getRemoteUtilityId();
  const { data: meterRunData } = useMeterRunList({
    remote_utility_id: remoteUtilityId,
  });

  // Set default selected run when data is loaded
  useEffect(() => {
    if (meterRunData?.result && meterRunData.result.length > 0 && !selectedRun) {
      setSelectedRun(meterRunData.result[0].id);
    }
  }, [meterRunData, selectedRun]);

  // Add hook for run-wise validation data
  const { data: runWiseValidationData } = useRunWiseReadingValidationDashboard({
    remote_utility_id: remoteUtilityId,
    meter_run_id: selectedRun,
  });

  const progressValue = Math.round(runWiseValidationData?.result?.completionPercentage || 0);

  const metrics = [
    {
      label: "Total Readings",
      value: runWiseValidationData?.result?.totalReads || 0,
    },
    {
      label: "Validated Reads",
      value: runWiseValidationData?.result?.validatedReads || 0,
    },
    {
      label: "Pending Review",
      value: runWiseValidationData?.result?.pendingValidation || 0,
    },
    {
      label: "Estimated",
      value: runWiseValidationData?.result?.validationEstimated || 0,
    },
    {
      label: "Exceptions",
      value: runWiseValidationData?.result?.validationExemptions || 0,
    },
    { label: "Exceptions Resolved", value: 0 },
    {
      label: "Adjusted",
      value: runWiseValidationData?.result?.validationAdjustment || 0,
    },
    {
      label: "Revisit",
      value: runWiseValidationData?.result?.validationRevisit || 0,
    },
  ];

  // Transform the data for dropdown options
  const dropdownOptions = [
    ...(meterRunData?.result?.map((run) => ({
      value: run.id,
      label: `${run.meterCycle} - ${run.month}`,
    })) || []),
  ];

  return (
    <Card className="p-6 bg-white rounded-2xl">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
        <h2 className="card-heading-text">
          Active Reading Cycle Validations
        </h2>

        <div className="flex items-center mt-2 sm:mt-0">
          <select
            className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={selectedRun}
            onChange={(e) => setSelectedRun(e.target.value)}
          >
            {dropdownOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex justify-between mb-2 items-center">
          <span className="text-sm font-medium">Progress</span>
          <span className="text-sm font-medium">{progressValue}% Complete</span>
        </div>
        <Progress value={progressValue} className="h-2" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <div key={index} className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-muted-foreground">{metric.label}</p>
            <p className="text-xl font-semibold mt-1">{metric.value}</p>
          </div>
        ))}
      </div>
    </Card>
  );
};